import React, { useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import store from '../../redux/store';

import { passwordValidation, hasValidationErrors } from '../../lib/airframe/FormValidation';
import { CiriumWrapper } from '../../components/cirium/CiriumWrapper';
import { TravelerRegisterPage } from './TravelerRegisterPage';

import { travelerRegister } from '../../service/travelerRegisterService';
import { travelerLogin } from '../../service/travelerLoginService';

import { selectImageUrl } from '../../redux/entities/brandingImage/brandingImageSelectors';
import { goTo } from '../../configTdm';
import { TravelerProfileLogin } from '../../appRoutes';

interface RegisterValues {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  kwijybo: string;
}

interface ErrorResponse {
  error: string;
  success?: boolean;
  pending?: boolean;
  redirectPath?: string;
}

const TravelerRegisterContainer: React.FC = () => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [isExistingUsername, setIsExistingUsername] = useState(false);
  const [displayExistingUsernameMsg, setDisplayExistingUsernameMsg] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const accountId = Number(queryParams.get('accountId'));
  const tripId = Number(queryParams.get('tripId'));
  const travelerKey = String(queryParams.get('travelerKey'));
  const brandingImageUrl = useSelector(selectImageUrl);

  const usernameValidation = (username: string) => {
    if (username.length > 50) {
      return 'Username must be shorter';
    }
    return true;
  };

  const travelerRegisterInfo = (values: RegisterValues) => ({
    accountId,
    travelerKey,
    username: values.username || values.email,
    email: values.email,
    password: values.password,
    confirmPassword: values.confirmPassword,
    kwijybo: values.kwijybo,
    tripId,
  });

  const handleServiceCallError = (addToast: (message: string, options: { variant: string }) => void, error: string) =>
    addToast(error.replace('Bad Request: ', ''), {
      variant: 'error',
    });

  const handleRegisterResponseFailure = (response: ErrorResponse, addToast: (message: string, options: { variant: string }) => void) => {
    if (response.error.includes('User already exists')) {
      setIsExistingUsername(true);
      setDisplayExistingUsernameMsg(true);
    } else {
      handleServiceCallError(addToast, response.error);
    }
  };

  const handleRegisterResponse = (response: ErrorResponse, addToast: (message: string, options: { variant: string }) => void, values: RegisterValues) => {
    if (response.success === false) {
      handleRegisterResponseFailure(response, addToast);
    } else if (response.pending === true) {
      goTo(TravelerProfileLogin);
    } else {
      handleTravelerLogin(values);
    }
  };

  const travelerLoginInfo = (values: RegisterValues) => ({
    username: values.email,
    password: values.password,
    kwijybo: values.kwijybo,
  });

  const handleTravelerLogin = (values: RegisterValues) => {
    travelerLogin(travelerLoginInfo(values))
      .then((loginResponse) => goTo(loginResponse.redirectPath))
      .catch((error) => goTo(error.redirectPath));
  };

  const handleTravelerRegister = (errors: any, values: RegisterValues, addToast: (message: string, options: { variant: string }) => void) => {
    if (!hasValidationErrors(errors)) {
      setFormDisabled(true);
      travelerRegister(travelerRegisterInfo(values))
        .then((response) => handleRegisterResponse(response, addToast, values))
        .catch((err) => {
          handleServiceCallError(addToast, err.message);
        })
        .finally(() => setFormDisabled(false));
    }
  };

  const closeExistingUsernameMsg = () => setDisplayExistingUsernameMsg(false);

  return (
    <CiriumWrapper loading={formDisabled}>
      <TravelerRegisterPage
        disabled={formDisabled}
        usernameValidation={usernameValidation}
        passwordValidation={passwordValidation}
        handleTravelerRegister={handleTravelerRegister}
        email={travelerKey}
        isExistingUsername={isExistingUsername}
        displayExistingUsernameMsg={displayExistingUsernameMsg}
        closeExistingUsernameMsg={closeExistingUsernameMsg}
        brandingImageUrl={brandingImageUrl}
      />
    </CiriumWrapper>
  );
};

const WrappedTravelerRegisterContainer: React.FC = () => (
  <Provider store={store}>
    <TravelerRegisterContainer />
  </Provider>
);

export default WrappedTravelerRegisterContainer;